import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate } from 'react-router-dom';
import { Settings2, Plus, Monitor, ArrowLeft } from 'lucide-react';
import { buildTestXml, epsonPrint, noAppToastArgs } from '@/lib/eposPrint';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const errMsg = (e, f = 'Failed') => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || 'Invalid input').join(', ');
  return typeof d === 'string' ? d : f;
};

const F = ({ label, children }) => (
  <div><Label className="text-xs text-wt-text-muted">{label}</Label><div className="mt-1">{children}</div></div>
);

export default function POSSetupPage() {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [newStore, setNewStore] = useState({ name: '', industry: 'retail' });
  const [regName, setRegName] = useState({});

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/pos/stores`, auth()); setStores(r.data.stores || []); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addStore = async () => {
    try {
      await axios.post(`${API}/pos/stores`, newStore, auth());
      toast.success('Store created'); setNewStore({ name: '', industry: 'retail' }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveStore = async (s, patch) => {
    try { await axios.put(`${API}/pos/stores/${s.id}`, patch, auth()); toast.success('Saved'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addRegister = async (storeId) => {
    try {
      await axios.post(`${API}/pos/registers`, { store_id: storeId, name: regName[storeId] || 'Register' }, auth());
      toast.success('Register added'); setRegName({ ...regName, [storeId]: '' }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="pos-setup-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="border-wt-border" onClick={() => navigate('/pos')} data-testid="pos-setup-back"><ArrowLeft className="w-4 h-4" /></Button>
        <Settings2 className="w-6 h-6 text-wt-gold" />
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">POS Setup</h1>
          <p className="text-wt-text-muted text-sm">Stores, registers, receipt customization & settings</p>
        </div>
      </div>

      {/* New store */}
      <div className="bg-wt-paper border border-wt-border rounded-xl p-4 flex gap-2 flex-wrap items-end" data-testid="new-store-card">
        <div className="flex-1 min-w-[200px]"><Label className="text-xs text-wt-text-muted">New store name</Label>
          <Input value={newStore.name} onChange={(e) => setNewStore({ ...newStore, name: e.target.value })} className="mt-1" data-testid="new-store-name" /></div>
        <select value={newStore.industry} onChange={(e) => setNewStore({ ...newStore, industry: e.target.value })}
          className="bg-wt-bg border border-wt-border rounded px-2 py-2 text-sm text-wt-text-primary h-10" data-testid="new-store-industry">
          <option value="retail">Retail</option><option value="grocery">Grocery</option><option value="restaurant">Restaurant</option>
        </select>
        <Button onClick={addStore} disabled={!newStore.name.trim()} data-testid="new-store-add"><Plus className="w-4 h-4 mr-1" />Add Store</Button>
      </div>

      {stores.map((s) => (
        <StoreCard key={s.id} store={s} onSave={saveStore}
          regName={regName[s.id] || ''} setRegName={(v) => setRegName({ ...regName, [s.id]: v })}
          onAddRegister={() => addRegister(s.id)} />
      ))}
      {stores.length === 0 && <p className="text-wt-text-muted text-sm text-center py-6">No stores yet — create your first store above</p>}
    </div>
  );
}

const StoreCard = ({ store, onSave, regName, setRegName, onAddRegister }) => {
  const [f, setF] = useState({
    name: store.name || '', address: store.address || '', phone: store.phone || '',
    trn: store.trn || '', industry: store.industry || 'retail',
    receipt_header: store.receipt_header || '', receipt_footer: store.receipt_footer || '',
    paper_width: store.paper_width || '80', show_customer_on_receipt: store.show_customer_on_receipt !== false,
    print_method: store.print_method || 'dialog',
    payment_methods: store.payment_methods || [],
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const fileRef = useRef(null);
  const [logo, setLogo] = useState(store.logo_url || '');
  const [uploading, setUploading] = useState(false);
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post(`${API}/pos/stores/${store.id}/logo`, fd, auth());
      setLogo(r.data.logo_url);
      toast.success('Logo uploaded — it will print at the top of receipts');
    } catch (er) { toast.error(errMsg(er)); }
    setUploading(false);
    e.target.value = '';
  };
  const removeLogo = async () => {
    try { await axios.delete(`${API}/pos/stores/${store.id}/logo`, auth()); setLogo(''); toast.success('Logo removed'); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="bg-wt-paper border border-wt-border rounded-xl p-5 space-y-3" data-testid={`store-card-${store.id}`}>
      <div className="grid sm:grid-cols-3 gap-3">
        <F label="Store name"><Input value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="store-name-input" /></F>
        <F label="Phone"><Input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></F>
        <F label="Industry">
          <select value={f.industry} onChange={(e) => set('industry', e.target.value)}
            className="w-full bg-wt-bg border border-wt-border rounded px-2 py-2 text-sm text-wt-text-primary" data-testid="store-industry-select">
            <option value="retail">Retail</option><option value="grocery">Grocery</option><option value="restaurant">Restaurant</option>
          </select>
        </F>
      </div>
      <F label="Address (printed on receipt)"><Input value={f.address} onChange={(e) => set('address', e.target.value)} /></F>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="TRN / VAT number"><Input value={f.trn} onChange={(e) => set('trn', e.target.value)} /></F>
        <F label="Receipt paper width">
          <select value={f.paper_width} onChange={(e) => set('paper_width', e.target.value)}
            className="w-full bg-wt-bg border border-wt-border rounded px-2 py-2 text-sm text-wt-text-primary" data-testid="paper-width-select">
            <option value="80">80mm (standard)</option><option value="58">58mm (compact)</option>
          </select>
        </F>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Receipt header line (above items)"><Input value={f.receipt_header} onChange={(e) => set('receipt_header', e.target.value)} placeholder="e.g. TAX INVOICE" data-testid="receipt-header-input" /></F>
        <F label="Receipt footer"><Input value={f.receipt_footer} onChange={(e) => set('receipt_footer', e.target.value)} data-testid="receipt-footer-input" /></F>
      </div>
      <F label="Custom payment methods (shown as buttons at checkout — e.g. 'ADCB terminal', 'Network machine 2', 'Payment link Tabby')">
        <div className="space-y-2">
          {(f.payment_methods || []).map((pm, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`pm-row-${i}`}>
              <Input value={pm.name} placeholder="Method name" className="h-9 flex-1"
                onChange={(e) => set('payment_methods', f.payment_methods.map((x, y) => y === i ? { ...x, name: e.target.value } : x))}
                data-testid={`pm-name-${i}`} />
              <select value={pm.type || 'card'}
                onChange={(e) => set('payment_methods', f.payment_methods.map((x, y) => y === i ? { ...x, type: e.target.value } : x))}
                className="bg-wt-bg border border-wt-border rounded px-2 h-9 text-sm text-wt-text-primary" data-testid={`pm-type-${i}`}>
                <option value="card">Card</option>
                <option value="link">Payment link</option>
                <option value="other">Other</option>
              </select>
              <Button size="sm" variant="ghost" className="text-red-400 h-8 px-2"
                onClick={() => set('payment_methods', f.payment_methods.filter((_, y) => y !== i))} data-testid={`pm-remove-${i}`}>✕</Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="border-wt-border"
            onClick={() => set('payment_methods', [...(f.payment_methods || []), { id: `pm-${Date.now()}`, name: '', type: 'card' }])}
            data-testid="pm-add-btn">+ Add payment method</Button>
          <p className="text-[10px] text-wt-text-muted">Cash and Store credit are always available. If no custom methods are added, default Card and Payment link buttons are shown.</p>
        </div>
      </F>
      <F label="Receipt printing method">
        <select value={f.print_method} onChange={(e) => set('print_method', e.target.value)}
          className="w-full bg-wt-bg border border-wt-border rounded px-2 py-2 text-sm text-wt-text-primary" data-testid="print-method-select">
          <option value="dialog">System print dialog (AirPrint / browser)</option>
          <option value="epson_tmassistant">Epson TM Print Assistant — direct Bluetooth/LAN (iPad, no dialog)</option>
        </select>
        {f.print_method === 'epson_tmassistant' && (
          <div className="mt-1 space-y-2">
            <p className="text-[11px] text-amber-400/90">
              Install the free "Epson TM Print Assistant" app from the App Store on the iPad, open it once and pair your TM-m30III over Bluetooth.
              Receipts then print automatically — no iOS print dialog. Note: the printed receipt is text-only (logo is skipped over Bluetooth).
              If you see "Safari cannot open the page because the address is invalid", the app is not installed on that device yet.
            </p>
            <Button size="sm" variant="outline" className="border-wt-border"
              onClick={() => epsonPrint(buildTestXml(f), () => toast.error(...noAppToastArgs()))}
              data-testid="epson-test-print-btn">
              Test print (on this device)
            </Button>
          </div>
        )}
      </F>
      <label className="flex items-center gap-2 text-xs text-wt-text-muted">
        <input type="checkbox" checked={f.show_customer_on_receipt} onChange={(e) => set('show_customer_on_receipt', e.target.checked)} data-testid="show-customer-checkbox" />
        Show customer name on receipt
      </label>
      <F label="Receipt logo (printed at the top of every receipt)">
        <div className="flex items-center gap-3">
          {logo
            ? <img src={logo.startsWith('http') ? logo : BACKEND + logo} alt="logo" className="h-12 w-auto bg-white rounded p-1" data-testid="store-logo-preview" />
            : <span className="text-xs text-wt-text-muted">No logo</span>}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
          <Button size="sm" variant="outline" className="border-wt-border" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
            {uploading ? 'Uploading…' : (logo ? 'Change logo' : 'Upload logo')}
          </Button>
          {logo && <Button size="sm" variant="ghost" className="text-red-400" onClick={removeLogo} data-testid="remove-logo-btn">Remove</Button>}
        </div>
      </F>
      <div className="flex items-center justify-between flex-wrap gap-2 border-t border-wt-border/50 pt-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Monitor className="w-4 h-4 text-cyan-400" />
          {(store.registers || []).map(r => <span key={r.id} className="text-xs bg-wt-bg/60 border border-wt-border/50 rounded px-2 py-1 text-wt-text-primary">{r.name}</span>)}
          <Input value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="New register" className="h-8 w-32 text-xs" data-testid="new-register-input" />
          <Button size="sm" variant="outline" className="border-wt-border h-8" onClick={onAddRegister} disabled={!regName.trim()} data-testid="add-register-btn">Add</Button>
        </div>
        <Button size="sm" onClick={() => onSave(store, f)} data-testid="save-store-btn">Save Store</Button>
      </div>
    </div>
  );
};
